import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';
import Button from '../Button/Button';
import Icon from '../Icon/Icon';
import PopupMenu from '../PopupMenu/PopupMenu';
import Gravity from '../../constants/Gravity';

/**
 * `Menu` is a button that displays a {@link PopupMenu} when clicking on it.
 *
 * The popup menu will be displayed to the south east of the button by default, but you can use the *gravity* prop to
 * set to any direction defined in
 * the [`Gravity`](https://github.com/vimond/vimond-core-components/blob/master/src/constants/Gravity.js) constants
 * file. The menu will always be displayed within the visible area of the screen if possible.
 *
 * Menu items can be created as direct children of the `Menu` component or you may use the *onCreateMenuItems* callback
 * to lazily create the items. The items will only be rendered when the popup menu is visible, but when menu items are
 * passed as children React will still spend time on creating the items. *onCreateMenuItems* prevents this as the items
 * will only be created when the user clicks on the menu button. This is the preferred way to handle menus inside cell
 * renderers.
 *
 * `Menu` will handle the visibility state of the {@link PopupMenu} internally, but you override this by setting the
 * *visible* prop.
 *
 * @property {string} buttonClass The class name of the menu button. Will be appended to "core-button menu-button". Use
 * this prop to e.g. set the menu button class to "primary" or "tertiary".
 * @property {string} className The class name of this component. Wwill be appended to the class name "core-menu". The
 * class name will also be set on the popup menu and postfixed with "-popup".
 * @property {node} children The menu items. Alternatively you can use *onCreateMenuItems* callback to lazily create the
 * menu items when the menu button is triggered.
 * @property {any} data A data attribute that will be passed to *onCreateMenuItems* when lazily creating menu items.
 * @property {boolean} disabled Disables the component. The menu button will not be clickable when the component is
 * disabled. Defaults to `false`.
 * @property {string} gravity Sets the preferred position of the menu relative to the menu button.
 * See [`Gravity`](https://github.com/vimond/vimond-core-components/blob/master/src/constants/Gravity.js) for available
 * options. Defaults to `Gravity.SOUTH`.
 * @property {node} icon An optional SVG icon for the menu button. Defaults to the "action-menu" icon in
 * **vimond-core-components**.
 * @property {string} iconName The name of the SVG icon to use for the menu button as an alternative to providing the
 * SVG icon directly.
 * @property {string} id The ID of this component.
 * @property {function} onClick A callback that is called when the user clicks on the menu button. If you call
 * `preventDefault()` on the event, the `Menu` component will not toggle the internal state.
 * @property {function} onClose A callback that is called when the menu is closed.
 * @property {function} onCreateMenuItems Menu items can be created lazily using this function. The item or items
 * returned from this function will be displayed in the menu. This is useful when you want to add menus to cells in
 * lists or tables and want to improve the performance of React. The callback will be passed whatever value the *data*
 * prop has so you can different menus based on the data you set.
 * @property {boolean} showOpenCloseIcon If set to `true`, the menu button will show an indicator of whether the menu is
 * open or closed. Defaults to `false`.
 * @property {node} title The title of the menu button. Can also be JSX if you need custom content. Custom styling is applied
 * to content with class "user-menu". Can be combined with the *icon* or *iconName* prop. Defaults to no title.
 * @property {boolean} visible Sets the visibility state of the popup menu. If this prop is not set, `Menu` will handle
 * the state internally.
 *
 * @see PopupMenu
 */
const Menu = ({ id, buttonClass, children, className, data, visible, disabled, icon, onClick, onCreateMenuItems, iconName, onClose, title, showOpenCloseIcon, ...rest }) => {
  const [isVisible, setVisible] = useState();
  const refAnchor = useRef();

  useEffect(() => {
    setVisible(visible);
  }, [visible]);

  const handleClick = e => {
    const changedVisible = !isVisible;
    if (!disabled) {
      if (onClick) {
        onClick(e, changedVisible);
      }
      if (!e.defaultPrevented) {
        setVisible(changedVisible);
      }
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const handleClose = (e, preventEvent = true) => {
    if (preventEvent) {
      e.preventDefault();
      e.stopPropagation();
    }
    setVisible(false);
    if (onClose) {
      onClose(e);
    }
  };

  let popupMenu;
  let openCloseIcon;

  if (isVisible) {
    popupMenu = (
      <PopupMenu {...rest} anchor={refAnchor.current.getDOMNode()} onClose={handleClose} className={className && `${className}-popup`}>
        {children || (onCreateMenuItems && onCreateMenuItems(data))}
      </PopupMenu>
    );
  }
  if (showOpenCloseIcon) {
    openCloseIcon = <Icon className="state-icon" name={isVisible ? 'collapse' : 'dropdown'} />;
  }
  return (
    <div id={id} className={cx('core-menu', className)}>
      <Button className={cx('menu-button', buttonClass, { active: isVisible })} onClick={handleClick}
        icon={icon} iconName={!title && !iconName ? 'action-menu' : iconName} ref={c => { if (c) refAnchor.current = c; }}
        disabled={disabled || (!children && !onCreateMenuItems && !onClick)}>
        {title}
        {openCloseIcon}
      </Button>
      {popupMenu}
    </div>
  );
}
Menu.propTypes = {
  buttonClass: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node,
  data: PropTypes.any, // eslint-disable-line
  disabled: PropTypes.bool,
  gravity: PropTypes.string,
  icon: PropTypes.node,
  iconName: PropTypes.string,
  id: PropTypes.string,
  onClick: PropTypes.func,
  onClose: PropTypes.func,
  onCreateMenuItems: PropTypes.func,
  showOpenCloseIcon: PropTypes.bool,
  title: PropTypes.node,
  visible: PropTypes.bool,
};
Menu.defaultProps = {
  gravity: Gravity.SOUTHEAST
};
Menu.displayName = "Menu";
export default Menu;